import type { PolicyDocument, PolicySection } from './types'
import { OPERATOR } from './operator'
import {
  CHILDREN,
  CRASHLYTICS_OVERSEAS_ROW,
  CRASHLYTICS_ROW,
  DISPOSAL,
  OFFICER,
  OVERSEAS_HEADERS,
  OVERSEAS_REFUSAL,
  PROCESSOR_HEADERS,
  R2_ITEMS_SUPPORT,
  R2_RETENTION_SUPPORT,
  REMEDIES,
  RETENTION_HEADERS,
  REVENUECAT_OVERSEAS_ROW,
  REVENUECAT_ROW,
  SECURITY,
  SPONSOR_ITEM_AUTO,
  SPONSOR_NOTE,
  SPONSOR_PURPOSE,
  SPONSOR_RETENTION_ROW,
  SUPPORT_ITEM_AUTO,
  SUPPORT_ITEM_INPUT,
  SUPPORT_NOTE,
  SUPPORT_PURPOSE,
  SUPPORT_RETENTION_ROW,
  admobOverseasRow,
  admobRow,
  changes,
  cloudflareOverseasRow,
  cloudflareRow,
  rights,
  table,
} from './common'

/**
 * Easy Air Screen Lite(광고 지원 라이트판) 개인정보 처리방침.
 *
 * 유료판(easy-air-screen.ts)과 같은 구조에 **광고(Google AdMob, 비맞춤)** 조항이 더해진다 —
 * 홈 화면 하단 배너와 연결 시작 시 전면광고. 광고 관련 문안은 siheomboso.ts 와 같은 원칙
 * (비맞춤 전용, 운영자는 광고 데이터를 받지 않음). 이 구조가 바뀌면 문서를 먼저 고친다
 * (docs/privacy-policy.md).
 */

const EFFECTIVE_DATE = 'August 20, 2026'
const LAST_REVISED = 'August 26, 2026'
const CRASH_RETENTION = '90 days'

const ADMOB_PURPOSE = 'Serving banner and interstitial ads'

const SECTIONS: readonly PolicySection[] = [
  {
    heading: 'Article 1. Purposes of processing personal data',
    paragraphs: [
      'The Operator processes personal data for the following purposes only. Should a purpose change, the Operator takes the steps required by Article 18 of the Personal Information Protection Act, including obtaining separate consent.',
    ],
    bullets: [
      'Keeping the service reliable — finding and fixing the places where the app crashes or errors',
      'Serving advertising — showing the ads that fund a free service. Ad data is processed by Google (AdMob); the Operator does not receive it',
      SUPPORT_PURPOSE,
      SPONSOR_PURPOSE,
    ],
  },
  {
    heading: 'Article 2. Personal data processed',
    paragraphs: ['The service works without an account, and the items below are everything processed.'],
    bullets: [
      '[Collected automatically] App diagnostics — Firebase Crashlytics installation identifier and Firebase installation ID, screen and action names, app version and build, device model, operating system and device state, error messages, stack traces, and crash traces',
      '[Collected automatically] Device information and advertising identifier used in serving ads — processed by Google (AdMob), which supplies the ads, for ad delivery and fraud prevention. The Operator does not receive this information',
      SUPPORT_ITEM_INPUT,
      SUPPORT_ITEM_AUTO,
      SPONSOR_ITEM_AUTO,
    ],
    note: 'No name, phone number, location, screen content, or mirrored video is collected. An email address is collected only where you type one into the support page yourself so that a reply can be sent, and a photograph only where you attach one to a support message yourself. Neither sensitive data under Article 23 nor unique identifying information under Article 24 of the Personal Information Protection Act is collected.',
  },
  {
    heading: 'Article 3. Handling of screen data, and device permissions',
    paragraphs: [
      'Screen mirroring is carried out entirely on your own devices. The connection between the phone and the computer stays within the same local network and passes through no external server. The core mirroring feature needs no internet connection; the internet is used only to send error diagnostics and to fetch ads.',
      'Separately from the core feature, a message you send yourself from the Support screen in Settings is transmitted to the Operator’s server. It contains no screen content or mirrored video: only the text you wrote, together with your app version, device model, operating system version, and language setting (Articles 2 and 4).',
    ],
    bullets: [
      'Android location permission (ACCESS_FINE_LOCATION) — required by the operating system on Android 12 and later in order to find devices on the local network. The Operator neither collects nor stores location data',
      'Android nearby devices permission (NEARBY_WIFI_DEVICES) — used only to find computers on the same network',
      'iOS local network permission — asked once, so the app can talk to a computer on the local network',
    ],
    note: 'None of these permissions is used to collect your location or any other personal data. Attaching a photograph to a support message needs no permission: the picker belongs to the operating system, and the app receives only the photographs you choose there.',
  },
  {
    heading: 'Article 4. Processing and retention periods',
    table: table(RETENTION_HEADERS, [
      ['App diagnostics', `${CRASH_RETENTION} from collection`, 'Your consent (processor retention cycle)'],
      [
        'Device information and advertising identifier used in serving ads',
        'Subject to Google’s retention policy',
        'Ad serving (not held by the Operator)',
      ],
      [...SUPPORT_RETENTION_ROW],
      [...SPONSOR_RETENTION_ROW],
    ]),
    note: `The Operator carries out no transaction that would create a retention duty under the Act on Consumer Protection in Electronic Commerce or similar legislation, so no personal data is kept separately under any statute. Deleting the app stops any further transmission of diagnostics and any further ads. ${SUPPORT_NOTE} ${SPONSOR_NOTE}`,
  },
  {
    heading: 'Article 5. Provision of personal data to third parties',
    paragraphs: [
      'The Operator processes personal data only within the purposes set out in Article 1 and provides it to third parties only with your consent or where Article 17 or Article 18 of the Personal Information Protection Act applies. Personal data is never sold.',
    ],
  },
  {
    heading: 'Article 6. Entrustment of personal data processing',
    paragraphs: ['The Operator entrusts personal data processing as follows.'],
    table: table(PROCESSOR_HEADERS, [
      [...CRASHLYTICS_ROW],
      cloudflareRow(R2_ITEMS_SUPPORT, R2_RETENTION_SUPPORT),
      [...REVENUECAT_ROW],
      admobRow(ADMOB_PURPOSE),
    ]),
    note: 'In each processing agreement, as required by Article 26 of the Personal Information Protection Act, the Operator sets out in writing the prohibition on processing beyond the stated purpose, technical and administrative safeguards, restrictions on sub-processing, supervision, and liability for damages, and supervises whether the processor handles personal data safely. If what is entrusted changes, this policy is updated and notice given in advance.',
  },
  {
    heading: 'Article 7. Transfer of personal data abroad',
    paragraphs: [
      'The processors named in Article 6 are located outside Korea, and the entrustment transfers personal data abroad as follows.',
    ],
    table: table(OVERSEAS_HEADERS, [
      [...CRASHLYTICS_OVERSEAS_ROW],
      cloudflareOverseasRow(R2_ITEMS_SUPPORT, R2_RETENTION_SUPPORT),
      [...REVENUECAT_OVERSEAS_ROW],
      admobOverseasRow(ADMOB_PURPOSE),
    ]),
    note: OVERSEAS_REFUSAL,
  },
  DISPOSAL,
  rights(
    [
      'Deleting the app — removing the app stops any further transmission of diagnostics and any further ads',
      'Deleting a support message — send us the reference number shown on the support page at the email address below and that message is deleted',
    ],
    'the text of support messages and app diagnostics',
  ),
  {
    heading:
      'Installation and operation of automatic collection devices, and how to refuse them (including advertising)',
    paragraphs: [
      'The service shows a banner ad at the bottom of the home screen and an interstitial ad when you start a connection, both served by Google LLC’s AdMob. The Operator requests non-personalized ads only, which means your behavioural data is not used for targeted advertising. Even for non-personalized ads, however, Google may process device information, an advertising identifier (ADID/IDFA), and coarse IP-based location for ad delivery, fraud prevention, and frequency capping. That information goes from your device directly to Google without passing through the Operator, and Google’s handling of it is governed by the Google Privacy Policy (https://policies.google.com/privacy).',
      'You can refuse this collection by resetting or deleting your advertising identifier in device settings — Android: Settings → Privacy → Ads; iOS: Settings → Privacy & Security → Apple Advertising. Refusing leaves every feature of the service available to you, and non-personalized ads may still appear. If you would rather have no ads at all, the paid edition (Easy Air Screen) is available.',
    ],
    note: 'The Operator uses no cookie, advertising identifier, web beacon, or other automatic collection device for any purpose other than serving those ads, and does not itself use collected personal data for ad targeting or profiling.',
  },
  {
    ...CHILDREN,
    note: 'The service asks children for no additional personal data, and requests non-personalized ads only. If the app runs into an error, the same diagnostics are collected automatically as for any other user.',
  },
  SECURITY,
  OFFICER,
  REMEDIES,
  changes(EFFECTIVE_DATE, [
    'August 26, 2026 — the photographs attached to support messages moved from the Operator’s own server disk to Cloudflare R2 object storage. Articles 6 and 7 were amended accordingly: image files are now held by a processor outside Korea. What is collected, why, and how long it is kept did not change',
    'August 26, 2026 — the support screen moved out of the app to a page at cottonray.com/support which the app opens inside itself. What is collected, why, and how long it is kept did not change',
    'August 24, 2026 — updated the purposes, items collected, and retention periods following the addition of photo attachments (up to three, chosen by you) and an optional email address to the in-app support screen, which the Operator uses to send a single reply from no-reply@cottonray.com',
  'August 24, 2026 — updated the purposes, items collected, retention periods, entrustment, and notice of transfers abroad following the introduction of the optional “buy the developer a coffee” sponsorship (in-app purchase; receipts verified by RevenueCat)',
    'August 15, 2026 — established (structured on Article 30 of the Personal Information Protection Act, including notice of transfers abroad and of advertising through Google AdMob, non-personalized)',
    'August 20, 2026 — updated the purposes, items collected, retention periods, and how to exercise your rights following the introduction of in-app support messages (feedback only, no contact details taken)',
    'August 19, 2026 — the site language changed to English and this policy was reissued in English. Nothing about how data is handled changed',
  ]),
]

export const EASY_AIR_SCREEN_LITE_POLICY: PolicyDocument = {
  slug: 'easy-air-screen-lite',
  title: 'Easy Air Screen Lite Privacy Policy',
  intro: `${OPERATOR}, establishes and publishes the following privacy policy for Easy Air Screen Lite (“the service”) under Article 30 of the Personal Information Protection Act of the Republic of Korea, in order to protect the personal data of its users and to deal promptly and smoothly with any grievance arising from it. The service is the free, ad-supported edition and runs over a local network between your own devices, with no backend server.`,
  effectiveDate: EFFECTIVE_DATE,
  lastRevisedDate: LAST_REVISED,
  sections: SECTIONS,
}
